import type { AdminVote, UUID } from './types';

/**
 * How the administrators decide things together.
 *
 * One rule, written once, used by everything that needs the administrators to
 * agree — adding an administrator, removing a circle from the tree:
 *
 *   • It carries on a **majority of the administrators who have been active
 *     lately**. Counting the absent ones would let a board that has drifted
 *     away freeze the family.
 *   • If that majority never arrives, an **unopposed** proposal carries itself
 *     after `timeoutDays`. That is the safety valve, not the normal route.
 *   • A single objection closes the timeout. Anything contested needs the real
 *     majority, so the valve can never be used to slip something past people.
 *   • Enough objections to make the majority impossible end it the other way,
 *     rather than leaving it open forever.
 *
 * Who counts as "active lately" is decided by the caller (the repository knows
 * what the family has been doing); this module only does the arithmetic.
 */
export interface ConsensusTally {
  approvals: number;
  objections: number;
  /** Approvals needed: more than half of the active administrators. */
  needed: number;
  activeAdmins: number;
  /** Whether the reader has already answered, and how. */
  myVote?: 'approve' | 'object';
  /**
   * Days until it carries unopposed. `null` when that route is closed — nobody
   * has approved yet, someone has objected, or it is already settled.
   */
  autoApproveInDays: number | null;
}

export const DAY_MS = 86_400_000;

export function tallyVotes(input: {
  votes: AdminVote[];
  /** Administrators whose vote counts towards the majority right now. */
  activeAdminIds: UUID[];
  createdAt: string;
  timeoutDays: number;
  /** Still open, or already settled (a settled proposal has no clock left). */
  open: boolean;
  myAccountId?: UUID | null;
  now?: number;
}): ConsensusTally {
  const { votes, activeAdminIds, createdAt, timeoutDays, open, myAccountId, now = Date.now() } = input;
  const eligible = new Set(activeAdminIds);
  const approvals = votes.filter((v) => v.vote === 'approve' && eligible.has(v.accountId)).length;
  const objections = votes.filter((v) => v.vote === 'object' && eligible.has(v.accountId)).length;
  const needed = Math.floor(activeAdminIds.length / 2) + 1;
  const daysOpen = (now - Date.parse(createdAt)) / DAY_MS;
  const timeoutOpen = open && objections === 0 && approvals > 0;
  return {
    approvals,
    objections,
    needed,
    activeAdmins: activeAdminIds.length,
    myVote: myAccountId ? votes.find((v) => v.accountId === myAccountId)?.vote : undefined,
    autoApproveInDays: timeoutOpen ? Math.max(0, Math.ceil(timeoutDays - daysOpen)) : null,
  };
}

/**
 * What the votes and the clock now say:
 *   'carry'   — a majority approved
 *   'timeout' — unopposed for long enough
 *   'decline' — too many objections for a majority to be possible
 *   'wait'    — nothing has been decided yet
 */
export function consensusOutcome(tally: ConsensusTally): 'carry' | 'timeout' | 'decline' | 'wait' {
  if (tally.approvals >= tally.needed && tally.approvals > tally.objections) return 'carry';
  if (tally.autoApproveInDays === 0) return 'timeout';
  if (tally.objections >= tally.needed) return 'decline';
  return 'wait';
}
